import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import authService from 'store/auth/service';
import { IUser } from './interfaces';
import { localStorageWrapper } from '../../functions/localStorage';
import userService from './service';

const onChangeSessionChange = (newToken: string | null) => {
  if (newToken) {
    localStorageWrapper.setAccessToken(newToken);
  }
};

const getUserFromStore = (): IUser | { [key: string]: any } => {
  const user = localStorageWrapper.getUser();
  return user ?? {};
};

export default createSlice({
  name: 'user',
  initialState: {
    ...getUserFromStore(),
    accessToken: localStorageWrapper.getAccessToken(),
    prevAccessToken: localStorageWrapper.getPrevAccessToken(),
  } as IUser & { prevAccessToken?: string },
  reducers: {
    setToken(state, action: PayloadAction<string>) {
      if (action.payload === state.prevAccessToken) {
        state.prevAccessToken = undefined;
        localStorageWrapper.removePrevAccessToken();
      } else {
        state.prevAccessToken = state.accessToken;
        localStorageWrapper.setPrevAccessToken(state.prevAccessToken);
      }
      state.accessToken = action.payload;
      onChangeSessionChange(state.accessToken);
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(
        userService.endpoints.updateUser.matchFulfilled,
        (state, { payload }) => {
          localStorageWrapper.setUser(JSON.stringify({ ...state, ...payload }));
          return { ...state, ...payload };
        }
      )
      .addMatcher(
        userService.endpoints.uploadAvatarUser.matchFulfilled,
        (state, { payload }) => {
          localStorageWrapper.setUser(JSON.stringify({ ...state, ...payload }));
          return { ...state, ...payload };
        }
      )
      .addMatcher(
        authService.endpoints.login.matchFulfilled,
        (_state, { payload }) => {
          onChangeSessionChange(payload.accessToken);
          return payload;
        }
      )
      .addMatcher(
        authService.endpoints.confirmToken.matchFulfilled,
        (_state, { payload }) => {
          onChangeSessionChange(payload.accessToken);
          localStorageWrapper.setUser(JSON.stringify(payload));
          return payload;
        }
      )
      .addMatcher(
        authService.endpoints.loginCandidate.matchFulfilled,
        (_state, { payload }) => {
          onChangeSessionChange(payload.accessToken);
          return payload;
        }
      )
      .addMatcher(
        authService.endpoints.unsecuredLoginCandidate.matchFulfilled,
        (_state, { payload }) => {
          onChangeSessionChange(payload.accessToken);
          return payload;
        }
      )
      .addMatcher(
        authService.endpoints.checkUser.matchFulfilled,
        (state, { payload }) => {
          const user = payload ? { ...state, ...payload } : ({} as IUser);
          localStorageWrapper.setUser(JSON.stringify(user));
          return user;
        }
      )
      .addMatcher(
        authService.endpoints.checkUserCandidate.matchFulfilled,
        (state, { payload }) => {
          const user = payload ? { ...state, ...payload } : ({} as IUser);
          localStorageWrapper.setUser(JSON.stringify(user));
          return user;
        }
      )
      .addMatcher(authService.endpoints.logout.matchFulfilled, () => {
        onChangeSessionChange(null);
        localStorageWrapper.clear();
        return {} as IUser;
      });
  },
});
